import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  Filler
} from 'chart.js';
import { BarChart, LineChart, MixedChart } from '../components/charts';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import LicenseGate from '../components/ui/LicenseGate';
import KPICard from '../components/ui/KPICard';
import apiClient from '../utils/apiClient';
import { formatNumber, formatPercentage } from '../utils/formatters';
import { getChartColors } from '../utils/themeHelpers';
import { useTheme } from '../contexts/ThemeContext';
import { logger } from '../utils/logger';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  ChartLegend,
  Filler
);

interface BenchmarkRow {
  yearMonth: string | number;
  branchcount: number;
  yearBranchCount: number;
  ordercount: number;
  lagordercount: number;
  currentMonthForYearOrderCount: number;
  lastYearOrdercount: number;
  OrderCountGrowth: number;
  YearOrderCountGrowth: number;
  itemcount: number;
  lagitemcount: number;
  currentMonthForYearitemCount: number;
  lastYearitemcount: number;
  ItemCountGrowth: number;
  yearItemCountGrowth: number;
  itemPrice: number;
  lagitemprice: number;
  currentMonthForYearitemprice: number;
  lastYearitemprice: number;
  revenueGrowth: number;
  yearRevenueGrowth: number;
  orderAVG: number;
  lagorderAVG: number;
  yearOrderAvg: number;
  lastYearOrderAvg: number;
  orderAvgGrowth: number;
  yearOrderAvgGrowth: number;
  ItemAvg: number;
  LagItemAvg: number;
  CurrentYearItemAvg: number;
  lastYearItemAVG: number;
  AvgItemGrowth: number;
  YearItemAvgGrowth: number;
  ItemPriceAVG: number;
  lagItemPriceAVG: number;
  currentyearItemPriceAvg: number;
  LastYearItemPriceAvg: number;
  ItemPriceAvgGrowth: number;
  yearItemPriceAvgGrowth: number;
  inflation: number;
  foodInflation: number;
}

interface BenchmarkResponse {
  success: boolean;
  data: BenchmarkRow[];
  inflationRate?: number;
}

const DEFAULT_INFLATION_RATE = 0.48;
const CURRENCY = 'تومان';

const toNumber = (value: unknown): number => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeRate = (value: unknown): number => {
  const numericValue = toNumber(value);
  return Math.abs(numericValue) > 1 ? numericValue / 100 : numericValue;
};

const PERSIAN_MONTH_LABELS: Record<string, string> = {
  '01': 'فروردین',
  '02': 'اردیبهشت',
  '03': 'خرداد',
  '04': 'تیر',
  '05': 'مرداد',
  '06': 'شهریور',
  '07': 'مهر',
  '08': 'آبان',
  '09': 'آذر',
  '10': 'دی',
  '11': 'بهمن',
  '12': 'اسفند',
};

const formatMonth = (yearMonth: string | number): string => {
  const value = String(yearMonth);
  if (value.length !== 6) return value;
  const year = value.slice(2, 4);
  const month = value.slice(4, 6);
  return `${PERSIAN_MONTH_LABELS[month] || month} ${year}`;
};

const formatCurrencyCompact = (value: number): string => {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000_000_000) return `${(value / 1_000_000_000_000).toFixed(1)} همت`;
  if (absValue >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} میلیارد`;
  if (absValue >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} میلیون`;
  return formatNumber(Math.round(value));
};

const formatCurrency = (value: number): string => `${formatNumber(Math.round(value))} ${CURRENCY}`;

const getYear = (yearMonth: string | number): string => String(yearMonth).slice(0, 4);

const safeDivide = (numerator: number, denominator: number): number => (
  denominator === 0 ? 0 : numerator / denominator
);

const calculateGrowth = (current: number, previous: number): number => (
  previous === 0 ? 0 : (current / previous) - 1
);

const getMonthlyInflationRate = (row: BenchmarkRow): number => (
  row.foodInflation || row.inflation || 0
);

const getRealRevenue = (row: BenchmarkRow, revenueField: 'itemPrice' | 'currentMonthForYearitemprice' = 'currentMonthForYearitemprice'): number => (
  safeDivide(row[revenueField], 1 + getMonthlyInflationRate(row))
);

const adjustForInflation = (value: number, inflationRate: number): number => (
  safeDivide(value, 1 + inflationRate)
);

const calculateStdDev = (values: number[]): number => {
  if (values.length === 0) return 0;
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + Math.pow(v - avg, 2), 0) / values.length;
  return Math.sqrt(variance);
};

interface MarketIndicatorsDashboardProps {
  view?: 'all' | 'productivity' | 'stability' | 'growth-comparison' | 'quarterly';
}

const MarketIndicatorsDashboard: React.FC<MarketIndicatorsDashboardProps> = ({ view = 'all' }) => {
  const { currentTheme } = useTheme();
  const colors = getChartColors(currentTheme);
  const [rows, setRows] = useState<BenchmarkRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBenchmarkData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get<BenchmarkResponse>('/benchmark/market');
        const benchmarkRows = (response.data.data || [])
          .map((row: any) => ({
            ...row,
            ordercount: toNumber(row.ordercount),
            itemcount: toNumber(row.itemcount),
            itemPrice: toNumber(row.itemPrice),
            branchcount: toNumber(row.branchcount),
            orderAVG: toNumber(row.orderAVG),
            ItemAvg: toNumber(row.ItemAvg),
            ItemPriceAVG: toNumber(row.ItemPriceAVG),
            lagordercount: toNumber(row.lagordercount),
            lagitemcount: toNumber(row.lagitemcount),
            lagitemprice: toNumber(row.lagitemprice),
            inflation: normalizeRate(row.inflation),
            foodInflation: normalizeRate(row.foodInflation),
          }))
          .sort((first: BenchmarkRow, second: BenchmarkRow) => 
            String(first.yearMonth).localeCompare(String(second.yearMonth))
          );

        setRows(benchmarkRows);
      } catch (err) {
        logger.error('Error fetching market indicators data', 'MarketIndicatorsDashboard', err);
        setError('خطا در بارگذاری داده‌های شاخص‌های بازار');
      } finally {
        setLoading(false);
      }
    };

    fetchBenchmarkData();
  }, []);

  const labels = useMemo(() => rows.map(row => formatMonth(row.yearMonth)), [rows]);
  const latest = rows[rows.length - 1];
  const rows1404 = useMemo(() => rows.filter(row => getYear(row.yearMonth) === '1404'), [rows]);
  const rows1403 = useMemo(() => rows.filter(row => getYear(row.yearMonth) === '1403'), [rows]);

  // Calculate compound annual inflation
  const annualInflationRate = useMemo(() => {
    const yearRows = rows1404.length > 0 ? rows1404 : rows;
    if (yearRows.length === 0) return DEFAULT_INFLATION_RATE;
    const compound = yearRows.reduce((prod, row) => {
      return prod * (1 + getMonthlyInflationRate(row));
    }, 1);
    return compound - 1;
  }, [rows1404, rows]);

  // KPI 1: Productivity per Branch
  const productivityPerBranch = useMemo(() => {
    if (rows.length === 0) return { current: 0, previous: 0, growth: 0 };
    
    const currentMonth = latest;
    const currentProd = currentMonth.branchcount > 0 
      ? currentMonth.itemPrice / currentMonth.branchcount 
      : 0;
    
    const sameMonthLastYear = rows1403.find(r => 
      String(r.yearMonth).slice(4) === String(currentMonth.yearMonth).slice(4)
    );
    const previousProd = sameMonthLastYear?.branchcount > 0
      ? sameMonthLastYear.itemPrice / sameMonthLastYear.branchcount
      : 0;
    
    return {
      current: currentProd,
      previous: previousProd,
      growth: calculateGrowth(currentProd, previousProd)
    };
  }, [rows, latest, rows1403]);

  // KPI 2: Items per Branch
  const itemsPerBranch = useMemo(() => {
    if (rows.length === 0) return 0;
    const current = latest;
    return current.branchcount > 0 ? current.itemcount / current.branchcount : 0;
  }, [rows, latest]);

  // KPI 3: Branch Growth
  const branchGrowth = useMemo(() => {
    if (rows.length === 0) return 0;
    return calculateGrowth(latest.branchcount, latest.yearBranchCount || latest.branchcount);
  }, [rows, latest]);

  // Report 1: Growth Stability Index
  const growthStability = useMemo(() => {
    if (rows.length < 2) return null;
    
    const growthRates = rows.map(r => r.OrderCountGrowth).filter(g => g !== 0);
    if (growthRates.length === 0) return null;
    
    const avg = growthRates.reduce((a, b) => a + b, 0) / growthRates.length;
    const stdDev = calculateStdDev(growthRates);
    
    return {
      averageGrowth: avg,
      volatility: stdDev,
      stabilityScore: stdDev < 0.1 ? 'پایدار' : stdDev < 0.2 ? 'متوسط' : 'ناپایدار',
      stabilityVariant: stdDev < 0.1 ? 'success' as const : stdDev < 0.2 ? 'warning' as const : 'error' as const
    };
  }, [rows]);

  // Report 2: Quarterly Trend (using Lag data - 3 months ago)
  const quarterlyTrend = useMemo(() => {
    if (rows.length < 4) return null;
    
    const latestRow = rows[rows.length - 1];
    const threeMonthsAgo = rows[rows.length - 4];
    
    return {
      orderTrend: calculateGrowth(latestRow.ordercount, threeMonthsAgo.ordercount),
      revenueTrend: calculateGrowth(latestRow.itemPrice, threeMonthsAgo.itemPrice),
      avgOrderTrend: calculateGrowth(latestRow.orderAVG, threeMonthsAgo.orderAVG),
      itemsTrend: calculateGrowth(latestRow.itemcount, threeMonthsAgo.itemcount),
    };
  }, [rows]);

  // Report 3: Monthly vs Yearly Growth Comparison
  const growthComparisonData = useMemo(() => ({
    labels,
    datasets: [
      {
        type: 'bar' as const,
        label: 'رشد ماهانه',
        data: rows.map(row => row.OrderCountGrowth),
        backgroundColor: `${colors[0]}99`,
        borderColor: colors[0],
        borderWidth: 1,
      },
      {
        type: 'line' as const,
        label: 'رشد سالانه (مقایسه با ۱۴۰۳)',
        data: rows.map(row => row.YearOrderCountGrowth),
        borderColor: colors[2],
        backgroundColor: `${colors[2]}33`,
        borderWidth: 3,
        tension: 0.35,
        fill: false,
      }
    ]
  }), [labels, rows, colors]);

  // Report 4: Productivity per Branch Trend
  const productivityTrendData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'فروش هر شعبه',
        data: rows.map(row => row.branchcount > 0 ? row.itemPrice / row.branchcount : 0),
        borderColor: colors[5],
        backgroundColor: `${colors[5]}44`,
        tension: 0.35,
        fill: true,
        metricType: 'money'
      },
      {
        label: 'سفارش هر شعبه',
        data: rows.map(row => row.branchcount > 0 ? row.ordercount / row.branchcount : 0),
        borderColor: colors[0],
        backgroundColor: `${colors[0]}44`,
        tension: 0.35,
        fill: false,
        metricType: 'count'
      }
    ]
  }), [labels, rows, colors]);

  // Report 5: Branch Count Growth
  const branchGrowthData = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'تعداد شعب فعال',
        data: rows.map(row => row.branchcount),
        borderColor: colors[7],
        backgroundColor: `${colors[7]}44`,
        tension: 0.35,
        fill: false,
        metricType: 'count'
      },
      {
        label: 'تعداد شعب سال قبل',
        data: rows.map(row => row.yearBranchCount || row.branchcount),
        borderColor: colors[3],
        borderDash: [5, 5],
        tension: 0.35,
        fill: false,
        metricType: 'count'
      }
    ]
  }), [labels, rows, colors]);

  const chartOptions = useMemo(() => ({
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw;
            if (context.dataset.metricType === 'money') {
              return `${context.dataset.label}: ${formatCurrency(value)}`;
            }
            if (context.dataset.metricType === 'count') {
              return `${context.dataset.label}: ${formatNumber(Math.round(value))}`;
            }
            return `${context.dataset.label}: ${formatNumber(Math.round(value))}`;
          }
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: (value: string | number) => formatCurrencyCompact(Number(value))
        }
      },
      x: {
        grid: { display: false }
      }
    }
  }), []);

  const percentChartOptions = useMemo(() => ({
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${formatPercentage(Number(context.raw) * 100)}`
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: (value: string | number) => `${Number(value) * 100}%`
        }
      },
      x: {
        grid: { display: false }
      }
    }
  }), []);

  const showProductivity = view === 'all' || view === 'productivity';
  const showStability = view === 'all' || view === 'stability';
  const showGrowthComparison = view === 'all' || view === 'growth-comparison';
  const showQuarterly = view === 'all' || view === 'quarterly';

  return (
    <LicenseGate licenseType="evaluation" dashboardName="شاخص‌های بازار">
      <div className="container-dashboard" dir="rtl">
        <div className="space-y-8 md:space-y-10 animate-enter-up">
          {loading ? (
            <div className="flex flex-col items-center justify-center min-h-[300px]" role="status" aria-live="polite">
              <LoadingSpinner
                variant="minimal"
                size="lg"
                message="در حال بارگذاری شاخص‌های بازار…"
              />
            </div>
          ) : error ? (
            <div className="p-4 m-6 bg-status-error text-text-inverse rounded-lg shadow-md" role="alert">
              <span className="font-medium">{error}</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="card-base card-padding text-center">
              <h1 className="title-dashboard">شاخص‌های بازار</h1>
              <p className="content-secondary">داده‌ای برای نمایش وجود ندارد.</p>
            </div>
          ) : (
            <>
              <div>
                <h1 className="title-dashboard">شاخص‌های مستقل بازار</h1>
                <p className="content-secondary mt-2">
                  تحلیل بهره‌وری، پایداری رشد و روندهای ۳ ماهه بازار کافه و رستوران.
                  آخرین ماه داده: {formatMonth(latest.yearMonth)}
                </p>
              </div>

              {/* KPI Section */}
              <section className="space-y-6">
                <div className="card-base card-padding">
                  <h2 className="title-section mb-4">شاخص‌های کلیدی عملکرد (KPI)</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                    <KPICard
                      title="فروش میانگین هر شعبه"
                      value={formatCurrency(productivityPerBranch.current)}
                      subtitle={`۱۴۰۳: ${formatCurrency(productivityPerBranch.previous)} | رشد سالانه: ${formatPercentage(productivityPerBranch.growth * 100)}`}
                      variant={productivityPerBranch.growth >= 0 ? 'success' : 'error'}
                      accentStyle="gradient"
                    />
                    <KPICard
                      title="اقلام به ازای هر شعبه"
                      value={formatNumber(Math.round(itemsPerBranch))}
                      subtitle="توزیع اقلام در شعب"
                      variant="info"
                      accentStyle="corner"
                    />
                    <KPICard
                      title="رشد تعداد شعب"
                      value={formatPercentage(branchGrowth * 100)}
                      subtitle={`فعال: ${formatNumber(latest.branchcount)} | سال قبل: ${formatNumber(latest.yearBranchCount || 0)}`}
                      variant={branchGrowth >= 0 ? 'success' : 'error'}
                      accentStyle="gradient"
                    />
                    {growthStability && (
                      <KPICard
                        title="پایداری رشد"
                        value={growthStability.stabilityScore}
                        subtitle={`نوسان: ${formatPercentage(growthStability.volatility * 100)} | میانگین رشد: ${formatPercentage(growthStability.averageGrowth * 100)}`}
                        variant={growthStability.stabilityVariant}
                        accentStyle="corner"
                      />
                    )}
                  </div>
                </div>
              </section>

              {/* Quarterly Trend Report */}
              {showQuarterly && quarterlyTrend && (
                <section className="space-y-6">
                  <div className="card-base card-padding">
                    <h2 className="title-section mb-4">گزارش روند ۳ ماهه</h2>
                    <p className="content-secondary mb-4">
                      مقایسه {formatMonth(latest?.yearMonth || '')} با {formatMonth(rows[rows.length - 4]?.yearMonth || '')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-text-tertiary">رشد سفارشات</p>
                        <p className={`title-card ${quarterlyTrend.orderTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercentage(quarterlyTrend.orderTrend * 100)}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-text-tertiary">رشد فروش</p>
                        <p className={`title-card ${quarterlyTrend.revenueTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercentage(quarterlyTrend.revenueTrend * 100)}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-text-tertiary">رشد میانگین سفارش</p>
                        <p className={`title-card ${quarterlyTrend.avgOrderTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercentage(quarterlyTrend.avgOrderTrend * 100)}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-text-tertiary">رشد اقلام</p>
                        <p className={`title-card ${quarterlyTrend.itemsTrend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercentage(quarterlyTrend.itemsTrend * 100)}
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Productivity Reports */}
              {showProductivity && (
                <section className="space-y-6">
                  <div className="card-base card-padding">
                    <h2 className="title-section mb-4">گزارش بهره‌وری شعب</h2>
                    <p className="content-secondary mb-6">
                      تحلیل فروش و سفارش به ازای هر شعبه در طول زمان نشان‌دهنده کارایی عملیاتی است.
                    </p>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                      <LineChart
                        title="روند بهره‌وری هر شعبه"
                        data={productivityTrendData}
                        options={chartOptions}
                        height={360}
                        className="card-base card-padding"
                      />
                      <LineChart
                        title="رشد تعداد شعب فعال"
                        data={branchGrowthData}
                        options={chartOptions}
                        height={360}
                        className="card-base card-padding"
                      />
                    </div>
                  </div>
                </section>
              )}

              {/* Growth Stability Report */}
              {showStability && growthStability && (
                <section className="space-y-6">
                  <div className="card-base card-padding">
                    <h2 className="title-section mb-4">گزارش پایداری رشد (تحلیل نوسان)</h2>
                    <div className="p-5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-6">
                      <p className="text-sm text-blue-800 dark:text-blue-200 leading-7">
                        <span className="font-semibold">تحلیل پایداری:</span>{' '}
                        {growthStability.stabilityScore === 'پایدار' 
                          ? 'رشد بازار بسیار پایدار است و نوسانات کمی دارد.'
                          : growthStability.stabilityScore === 'متوسط'
                          ? 'رشد بازار نوسانات متوسطی دارد که نیاز به پایش دارد.'
                          : 'رشد بازار ناپایدار است و نوسانات زیادی دارد که نشان‌دهنده عدم قطعیت در بازار است.'}
                        {' '}میانگین رشد ماهانه {formatPercentage(growthStability.averageGrowth * 100)} با نوسان {formatPercentage(growthStability.volatility * 100)} است.
                      </p>
                    </div>
                    <BarChart
                      title="نرخ رشد ماهانه سفارشات (تحلیل نوسان)"
                      data={{
                        labels,
                        datasets: [{
                          label: 'رشد ماهانه',
                          data: rows.map(r => r.OrderCountGrowth),
                          backgroundColor: rows.map(r => 
                            Math.abs(r.OrderCountGrowth - growthStability.averageGrowth) > growthStability.volatility
                              ? `${colors[3]}99`
                              : `${colors[0]}99`
                          ),
                          borderColor: colors[0],
                          borderWidth: 1,
                        }]
                      }}
                      options={percentChartOptions}
                      height={360}
                      className="card-base card-padding"
                    />
                  </div>
                </section>
              )}

              {/* Growth Comparison Report */}
              {showGrowthComparison && (
                <section className="space-y-6">
                  <div className="card-base card-padding">
                    <h2 className="title-section mb-4">گزارش مقایسه رشد ماهانه و سالانه</h2>
                    <p className="content-secondary mb-6">
                      مقایسه رشد ماهانه (نسبت به ماه قبل) با رشد سالانه (نسبت به ماه مشابه سال قبل) برای درک بهتر روندها.
                    </p>
                    <MixedChart
                      title="مقایسه رشد ماهانه vs سالانه سفارشات"
                      data={growthComparisonData}
                      options={percentChartOptions}
                      height={400}
                      className="card-base card-padding"
                    />
                  </div>
                </section>
              )}

              {/* Market Summary */}
              <section className="card-base card-padding">
                <h2 className="title-section mb-4">خلاصه تحلیلی بازار</h2>
                <div className="p-5 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-200 leading-7">
                    <span className="font-semibold">نکات کلیدی:</span>
                  </p>
                  <ul className="list-disc list-inside mt-3 space-y-2 text-sm text-amber-800 dark:text-amber-200">
                    <li>
                      بهره‌وری هر شعبه {formatPercentage(productivityPerBranch.growth * 100)} تغییر یافته 
                      ({formatCurrency(productivityPerBranch.current)} در مقابل {formatCurrency(productivityPerBranch.previous)}).
                    </li>
                    {quarterlyTrend && (
                      <li>
                        در ۳ ماه اخیر، سفارشات {formatPercentage(quarterlyTrend.orderTrend * 100)} و 
                        فروش {formatPercentage(quarterlyTrend.revenueTrend * 100)} تغییر یافته است.
                      </li>
                    )}
                    {growthStability && (
                      <li>
                        پایداری رشد بازار <strong>{growthStability.stabilityScore}</strong> ارزیابی می‌شود 
                        (نوسان: {formatPercentage(growthStability.volatility * 100)}).
                      </li>
                    )}
                    <li>
                      تورم سالانه مرکب: {formatPercentage(annualInflationRate * 100)}.
                    </li>
                  </ul>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </LicenseGate>
  );
};

export default MarketIndicatorsDashboard;
